/**
 * Inline card entry form using Stripe Elements. Renders inside the payment
 * method picker when the user clicks "Add card".
 *
 * The parent is responsible for creating the SetupIntent and passing it through
 * whole. This component just handles the Stripe Elements UI.
 */
import * as Sentry from '@sentry/react'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useEffect, useState } from 'react'

import { type PostMemberSetupIntentResponse, REGIONS_BY_ID } from '@snootsvet/shared'

import { Button } from '@/frontend/components/ui/button'
import { PUBLISHABLE_KEY_VARS, stripeForRegion } from '@/frontend/lib/stripe'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface AddPaymentMethodFormProps {
  /**
   * The SetupIntent exactly as `POST /members/:id/setup-intents` returned it —
   * secret and minting region together, deliberately not two props.
   *
   * A secret can only be confirmed by the Stripe account that created it, and a
   * mismatch fails silently until the user submits their card. Passing the whole
   * response makes that pairing structural: there is no way to source the secret
   * from one place and the region from another (e.g. `useViewerRegion()`).
   */
  setupIntent: PostMemberSetupIntentResponse
  /** Called after a card is successfully saved. */
  onSuccess: () => void
  /** Called when the user cancels. */
  onCancel: () => void
}

/**
 * Inline card entry tray. Wraps Stripe Elements with the SetupIntent's secret and
 * renders the PaymentElement for card collection.
 */
export function AddPaymentMethodForm({
  setupIntent,
  onSuccess,
  onCancel,
}: AddPaymentMethodFormProps) {
  const { clientSecret, region } = setupIntent
  const stripe = stripeForRegion(region)

  // A missing key is a deploy mistake, not a user error, so report it rather than
  // relying on someone noticing the panel below — a forgotten
  // VITE_STRIPE_US_PUBLISHABLE_KEY is otherwise invisible until a staff member
  // happens to open the card tray. In an effect because `stripeForRegion` is called
  // during render, and reporting is a side effect.
  useEffect(() => {
    if (stripe) return
    Sentry.captureMessage(
      `Stripe publishable key missing for region "${region}" — card entry disabled`,
      'error'
    )
  }, [stripe, region])

  if (!stripe) {
    // `region` is typed `RegionId`, but deploys aren't atomic: frontend and backend
    // are separate services, so a new bundle can briefly talk to a backend that
    // predates the region field. Degrade to "this region" instead of throwing on
    // `undefined` and taking the whole checkout screen down with the error boundary.
    const regionName = REGIONS_BY_ID[region]?.name
    return (
      <p className="rounded-lg border border-dashed p-4 text-center text-destructive-fg">
        Stripe is not configured for {regionName ? `the ${regionName}` : 'this region'}. Set{' '}
        {PUBLISHABLE_KEY_VARS[region] ?? 'the region’s publishable key'} to enable card entry.
      </p>
    )
  }

  return (
    <Elements stripe={stripe} options={{ clientSecret }}>
      <CardEntryFields onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  )
}

// ---------------------------------------------------------------------------
// Internal — must be inside <Elements>
// ---------------------------------------------------------------------------

function CardEntryFields({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setIsSubmitting(true)
    setError(null)

    const result = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    })

    setIsSubmitting(false)
    if (result.error) {
      setError(result.error.message ?? 'Something went wrong')
    } else {
      onSuccess()
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-lg border border-dashed p-4">
      {/*
        Wallets are not payment method types, so the SetupIntent's
        `payment_method_types: ['card']` doesn't reach them — Link, Google Pay and
        Apple Pay all ride on top of `card` and default to 'auto'. Turning them off
        has to happen here, client-side. (Link is what surfaced the "Bank" option:
        it's a funding source inside Link's own UI, not a payment method on the intent.)

        This tray is staff typing a member's card in at the front desk, so a wallet
        is never the thing being added.
      */}
      <PaymentElement
        options={{ wallets: { link: 'never', googlePay: 'never', applePay: 'never' } }}
      />

      {error && <p className="text-destructive-fg">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="ghost-destructive"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" variant="ghost" disabled={!stripe || isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Save Card'}
        </Button>
      </div>
    </form>
  )
}
